import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { revealField } from '@/lib/passengers/api';
import type { DisruptionPnrGroupRow } from '@/lib/disruptions/types';

// The passengers, by their real names, for the one surface that needs them.
//
// Everywhere else in this console a name is masked by default and revealed one
// at a time on operator intent (`revealable-name.tsx`, TDS §14). A printed
// voucher is the exception, and it is not a loosening of that rule — it is the
// rule's whole purpose. The document exists to be checked against a passport at
// a hotel front desk or an airline counter; "J*** P***" cannot be checked
// against anything, so a masked voucher is not a safer voucher, it is a useless
// one that sends the passenger back to the queue.
//
// So the names come through the SAME audited road every other reveal uses:
// `POST …/passengers/:urn/reveal/name`, which writes a PASSENGER_PII_REVEAL
// record naming the operator, the case and the passenger, and never logs the
// value. Opening a print sheet is the intent; the audit says a voucher was
// printed for these people, by this operator, at this time — which is exactly
// what an airline needs to be able to answer later.
//
// NEVER BLOCK ON IT. Each reveal is settled independently and a failure falls
// back to the name the roster already carried. A voucher that cannot be printed
// because an audit endpoint was slow is a passenger left standing at a counter.

/** How many names one sheet will reveal — a PNR group, not a flight manifest. */
const MAX_REVEALS = 20;

export interface PrintPassenger {
  paxUrn: string;
  /** Full name as it goes on paper. */
  name: string;
  /** True when this came back from the audited reveal rather than the roster. */
  revealed: boolean;
}

export interface PrintPassengers {
  /** Lead first, then the rest of the group in roster order. */
  passengers: PrintPassenger[];
  /** The reveal round-trip has settled (or was never needed). */
  isFetched: boolean;
}

export function usePrintPassengers(
  group: DisruptionPnrGroupRow,
  enabled: boolean,
): PrintPassengers {
  const roster = useMemo(() => {
    const rows = group.passengers ?? [];
    // The lead goes first: it is the name the counter reads out and the name the
    // hotel reservation is under. `leadName` is the same first+last the roster
    // row carries, so matching on it needs no second lookup.
    const lead = group.leadName?.trim();
    if (!lead) return rows;
    return [...rows].sort((a, b) => {
      const aLead = a.fullName?.trim() === lead ? 0 : 1;
      const bLead = b.fullName?.trim() === lead ? 0 : 1;
      return aLead - bLead;
    });
  }, [group.passengers, group.leadName]);

  const targets = roster.slice(0, MAX_REVEALS);

  const query = useQuery({
    queryKey: ['cases', group.caseUrn, 'pnr', group.subCaseUrn, 'print-names'],
    queryFn: async (): Promise<Record<string, string>> => {
      const settled = await Promise.allSettled(
        targets.map((p) => revealField(group.caseUrn, p.paxUrn, 'name')),
      );
      const byUrn: Record<string, string> = {};
      settled.forEach((result, i) => {
        const paxUrn = targets[i]?.paxUrn;
        if (paxUrn && result.status === 'fulfilled' && result.value.value) {
          byUrn[paxUrn] = result.value.value;
        }
      });
      return byUrn;
    },
    enabled: enabled && targets.length > 0,
    // A reveal is an audited event, not a cache-warming read: it fires once per
    // sheet, never on a window refocus, and never again while the same sheet is
    // open. One printed voucher, one audit line per passenger.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const passengers = useMemo<PrintPassenger[]>(() => {
    const lead: PrintPassenger[] = group.leadName
      ? [{ paxUrn: '', name: group.leadName, revealed: false }]
      : [];
    if (!roster.length) return lead;

    const named = roster
      .map((p) => {
        const revealed = query.data?.[p.paxUrn];
        return {
          paxUrn: p.paxUrn,
          name: (revealed ?? p.fullName ?? '').trim(),
          revealed: !!revealed,
        };
      })
      .filter((p) => p.name.length > 0);

    // A roster row with neither a revealed name nor one of its own leaves the
    // voucher nameless, which is worse than the masked name it replaced — the
    // group's lead is the fallback of last resort rather than a dash on paper.
    return named.length ? named : lead;
  }, [roster, group.leadName, query.data]);

  return {
    passengers,
    isFetched: !enabled || targets.length === 0 || query.isFetched,
  };
}
